import { useRef, useEffect } from 'react';
import { useLocalStorageState } from 'ahooks';
import type { Options as AhooksOptions } from 'ahooks/lib/createUseStorageState';
import { useLocation } from 'umi';

// 模块类型
type ModuleType = string;

// 本地存储项目类型
interface LocalStoreItem {
  key: string;
  module?: ModuleType;
}

// 模块映射存储键
const MODULE_MAPPING_KEY = 'QDESK_MODULE_MAPPING';

// 从localStorage加载模块映射
const loadModuleMapping = (): Record<string, string> => {
  try {
    const mapping = localStorage.getItem(MODULE_MAPPING_KEY);
    return mapping ? JSON.parse(mapping) : {};
  } catch (e) {
    console.error('Failed to load module mapping', e);
    return {};
  }
};

// 保存模块映射到localStorage
const saveModuleMapping = (mapping: Record<string, string>) => {
  try {
    localStorage.setItem(MODULE_MAPPING_KEY, JSON.stringify(mapping));
  } catch (e) {
    console.error('Failed to save module mapping', e);
  }
};

// 添加键到模块映射
const addToModuleMapping = (key: string, module?: ModuleType) => {
  if (!module) return;

  const mapping = loadModuleMapping();
  mapping[key] = module;
  saveModuleMapping(mapping);
};

// 从模块映射中移除键
const removeFromModuleMapping = (key: string) => {
  const mapping = loadModuleMapping();
  delete mapping[key];
  saveModuleMapping(mapping);
};

// runtime
const localStoreItems: LocalStoreItem[] = [];

// 初始化运行时内存中的localStoreItems
const initLocalStoreItems = () => {
  // 清空当前内存中的项
  localStoreItems.length = 0;

  // 从localStorage加载模块映射
  const moduleMapping = loadModuleMapping();

  // 遍历所有localStorage项
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key !== MODULE_MAPPING_KEY) {
      localStoreItems.push({
        key,
        module: moduleMapping[key]
      });
    }
  }
};

// 初始化运行时内存中的项
initLocalStoreItems();

/**
 *
 * @param name
 * @param options 配置项
 * @param usePrefix 是否使用前
 * @param module 所属模块名
 * @returns useLocalStorageState 相同的返回值
 */
export const useLocalStore = <T,>(
  name: string,
  options?: AhooksOptions<T>,
  usePrefix: boolean = true,
  module?: ModuleType
) => {
  // 获取用户名和路径
  const userName = localStorage.getItem('QDESK_USER_NAME') || 'default';
  const routerPath = useLocation().pathname;
  const storeNamePrefix = `${userName}_${routerPath}_`;

  // 最终的键名
  const finalKey = usePrefix ? `${storeNamePrefix}${name}` : name;

  // 记录存储项
  const keyRef = useRef(finalKey);
  const moduleRef = useRef(module);

  // 只注册一次并持久化模块信息
  useEffect(() => {
    const key = keyRef.current;
    const module = moduleRef.current;

    // 检查是否已存在于运行时内存
    const exists = localStoreItems.some(item => item.key === key);
    if (!exists) {
      // 添加到运行时内存
      localStoreItems.push({
        key,
        module
      });

      // 持久化模块信息
      if (module) {
        addToModuleMapping(key, module);
      }
    }
  }, []);

  return useLocalStorageState<T>(finalKey, options);
};

/**
 * 清除所有本地存储
 */
export const clearAllLocalStore = (): void => {
  // 清除所有项
  localStoreItems.forEach(item => {
    localStorage.removeItem(item.key);
    removeFromModuleMapping(item.key);
  });

  // 清空运行时内存
  localStoreItems.length = 0;

  // 清空模块映射
  saveModuleMapping({});
};

/**
 * 清除指定模块的所有本地存储
 * @param module 模块名称
 */
export const clearModuleLocalStore = (module: ModuleType): void => {
  // 从模块映射中获取所有属于该模块的键
  const moduleMapping = loadModuleMapping();
  const keysToRemove: string[] = [];

  // 查找所有属于该模块的键
  for (const [key, mappedModule] of Object.entries(moduleMapping)) {
    if (mappedModule === module) {
      keysToRemove.push(key);
    }
  }

  // 删除存储项
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    removeFromModuleMapping(key);

    // 从运行时内存中移除
    const index = localStoreItems.findIndex(item => item.key === key);
    if (index !== -1) {
      localStoreItems.splice(index, 1);
    }
  });

};

/**
 * 清除指定的本地存储
 * @param key 存储键名
 */
export const clearLocalStore = (key: string): void => {
  localStorage.removeItem(key);
  removeFromModuleMapping(key);

  // 从运行时内存中移除
  const index = localStoreItems.findIndex(item => item.key === key);
  if (index !== -1) {
    localStoreItems.splice(index, 1);
  }
};

/**
 * 获取当前用户和路径的前缀
 * @returns 存储前缀
 */
export const getStorePrefix = (): string => {
  const userName = localStorage.getItem('QDESK_USER_NAME') || 'default';
  const routerPath = window.location.pathname;
  return `${userName}_${routerPath}_`;
};
